<template>
  <el-row :gutter="15">
    <el-col
      :xs="6"
      :sm="5"
      :md="5"
      :lg="4"
      :xl="4"
    >
      <Filters
        ref="filters"
        v-model="filters"
        :resource="$sdk.edi"
        :primary-fields="['consignment', 'account', 'carrier', 'export_status', 'import_file_name', 'import_manifest_no', 'edi_status']"
        @sort="sort = $event; reload()"
        @apply="reload"
        @ready="load"
      />
    </el-col>

    <el-col
      :xs="18"
      :sm="19"
      :md="19"
      :lg="20"
      :xl="20"
    >
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <EDITable
          :edis="edis"
          :processing-edi-ids="processingEdiIds"
          @resend="resendEDI"
          @regenerate="regenerateEDI"
          @unlock="unlockEDI"
          @view-consignments="viewConsignments"
          @download-file="downloadEDIFile"
          @download-error-file="downloadErrorFile"
          @download-manifest="downloadManifestPdf"
          @print-manifest="printManifest"
          @ready="load"
        >
          <template v-slot:reference="{ edi }">
            {{ edi.id }}
          </template>
        </EDITable>

        <el-pagination
          class="text-center pt-3"
          :current-page.sync="edis.page"
          :page-size.sync="edis.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="edis.total"
          @size-change="reload"
          @current-change="reload"
        />
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
import EDITable from '@/components/Application/Freight/EDI/Table'
import Filters from '@/components/Filters/Simplified'
import Request, { GET } from '@/plugins/sdk/Request'

export default {
  components: {
    EDITable,
    Filters
  },
  data () {
    return {
      title: 'EDI',
      breadcrumb: {
        name: 'EDI',
        route: { name: 'freight-edi-v3' }
      },
      actions: [
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.reload
        }
      ],
      isLoading: true,
      filters: [],
      edis: [],
      processingEdiIds: [],
      sort: {
        field: null,
        direction: 'asc'
      }
    }
  },
  created () {
    this.load()
  },
  methods: {
    buildRequest (request) {
      request.filters = [
        ...this.filters
      ]
      request.include = [
        'account',
        'carrier',
        'file',
        'error_file'
      ]
      request.order = this.sort.field
        ? `${this.sort.field}:${this.sort.direction || 'asc'}`
        : 'created_at:desc'
    },
    withProcessing (edi, callback) {
      if (!edi || !edi.id) {
        return
      }

      this.processingEdiIds.push(edi.id)

      return Promise
        .resolve(callback())
        .finally(() => {
          this.processingEdiIds = this.processingEdiIds.filter(id => id !== edi.id)
        })
    },
    async updateExportStatus (edi, status, actionLabel) {
      try {
        await this.withProcessing(edi, async () => {
          edi.export_status = status
          await edi.update()
          await edi.refresh(request => {
            request.include = [
              'account',
              'carrier',
              'file',
              'error_file'
            ]
          })
        })

        this.$message.success(`EDI ${actionLabel} requested`)
      } catch (error) {
        this.$message.error(`Unable to ${actionLabel.toLowerCase()} EDI`)
      }
    },
    async load () {
      this.isLoading = true

      try {
        this.edis = await this
          .$sdk
          .edi
          .list(request => {
            this.buildRequest(request)
            request.pagination = 'simple'
          })
      } catch (error) {
        this.$message.error('Unable to load EDI records')
      } finally {
        this.isLoading = false
      }
    },
    async reload () {
      this.isLoading = true

      try {
        await this
          .edis
          .reload(request => {
            this.buildRequest(request)
          })
      } catch (error) {
        this.$message.error('Unable to refresh EDI records')
      } finally {
        this.isLoading = false
      }
    },
    async resendEDI (edi) {
      await this.updateExportStatus(edi, 2, 'Resend')
    },
    async regenerateEDI (edi) {
      await this.updateExportStatus(edi, 0, 'Regenerate')
    },
    async unlockEDI (edi) {
      try {
        await this.withProcessing(edi, async () => {
          edi.edi_status = 0
          await edi.update()
          await edi.refresh(request => {
            request.include = [
              'account',
              'carrier',
              'file',
              'error_file'
            ]
          })
        })

        this.$message.success('EDI unlock requested')
      } catch (error) {
        this.$message.error('Unable to unlock EDI')
      }
    },
    downloadEDIFile (edi) {
      if (!edi || !edi.file || !edi.file.id) {
        this.$message.warning('No file is available for this EDI record')
        return
      }

      this.$file.download(edi.file.id)
    },
    downloadErrorFile (edi) {
      if (!edi || !edi.error_file || !edi.error_file.id) {
        this.$message.warning('No error file is available for this EDI record')
        return
      }

      this.$file.download(edi.error_file.id)
    },
    downloadManifestPdf (edi) {
      this.$message({
        message: `Manifest PDF download is not implemented yet for ${edi.import_manifest_no || edi.id}`,
        type: 'warning'
      })
    },
    async ensureConsignmentsLoaded (edi) {
      if (!edi || !edi.id) {
        console.warn('Invalid EDI record provided for loading consignments')
        return false
      }

      if (Array.isArray(edi.consignments) && edi.consignments.length > 0) {
        console.log('Consignments already loaded for EDI', edi.id)
        return true
      }

      if (!edi.import_cons) {
        console.log('No consignments to load for EDI', edi.id)
        edi.consignments = []
        return true
      }

      try {
        console.log('Loading consignments for EDI', edi.id)
        await this.withProcessing(edi, async () => {
          await edi.refresh(request => {
            request.include = [
              'account',
              'carrier',
              'file',
              'error_file',
              'consignments',
              'consignments.service'
            ]
          })
        })

        return true
      } catch (error) {
        this.$message.error('Unable to load consignments for this EDI')
        return false
      }
    },
    async viewConsignments (edi) {
      await this.ensureConsignmentsLoaded(edi)
    },
    async printManifest (edi) {
      const loaded = await this.ensureConsignmentsLoaded(edi)

      if (!loaded) {
        return
      }

      const consignment = (edi && edi.consignments && edi.consignments.length)
        ? edi.consignments[0]
        : null

      if (!consignment || !consignment.id) {
        this.$message.warning('No consignments available to print a manifest')
        return
      }

      try {
        const request = Object.assign(new Request(), {
          _pathname: `/legacy/manifest?consignment=${consignment.id}`,
          method: GET
        })
        const response = await fetch(await request.build())
        const data = await response.json()

        if (data.manifest_pdf) {
          window.open(atob(data.manifest_pdf), '_blank')
          return
        }

        this.$message.error('No manifest found for this EDI')
      } catch (error) {
        this.$message.error('Unable to print manifest')
      }
    }
  }
}
</script>
